import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import MessageTick from './MessageTick';
import { Menu, MenuItem, IconButton, ListItemIcon, ListItemText, Tooltip } from '@mui/material';
import {
   ChevronDown,
   Info,
   Reply,
   Copy,
   Forward,
   Pin,
   Star,
   Trash2,
   Smile
} from 'lucide-react';
import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import isYesterday from 'dayjs/plugin/isYesterday';

dayjs.extend(isToday);
dayjs.extend(isYesterday);

const ChatMessages = ({
   messages,
   deleteMessage,
   togglePinMessage,
   toggleStarMessage,
   setReplyingTo
}) => {
   const lastMessageRef = useRef(null);
   const [anchorEl, setAnchorEl] = useState(null);
   const [selectedMessage, setSelectedMessage] = useState(null);

   useEffect(() => {
      lastMessageRef.current?.scrollIntoView({ behavior: 'smooth' });
   }, [messages]);

   const handleOpenMenu = (event, message) => {
      setAnchorEl(event.currentTarget);
      setSelectedMessage(message);
   };

   const handleCloseMenu = () => {
      setAnchorEl(null);
      setSelectedMessage(null);
   };

   const handleCopy = () => {
      if (selectedMessage) {
         navigator.clipboard.writeText(selectedMessage.content);
      }
      handleCloseMenu();
   };

   const handleReply = () => {
      if (selectedMessage) {
         setReplyingTo(selectedMessage);
      }
      handleCloseMenu();
   };

   const handleDelete = () => {
      if (selectedMessage && !selectedMessage.is_deleted) {
         deleteMessage(selectedMessage.id);
      }
      handleCloseMenu();
   };

   const handlePin = () => {
      if (selectedMessage) {
         togglePinMessage(selectedMessage.id, selectedMessage.is_pinned);
      }
      handleCloseMenu();
   };

   const handleStar = () => {
      if (selectedMessage) {
         toggleStarMessage(selectedMessage.id, selectedMessage.is_starred);
      }
      handleCloseMenu();
   };

   // Helper to format date separators
   const getDateLabel = (date) => {
      const d = dayjs(date);
      if (d.isToday()) return 'Today';
      if (d.isYesterday()) return 'Yesterday';
      if (d.isAfter(dayjs().subtract(7, 'day'))) return d.format('dddd');
      return d.format('MMMM D, YYYY');
   };

   const findMessageById = (id) => messages.find(m => m.id === id);

   return (
      <div className="flex flex-col gap-2 p-2">
         {messages.map((message, index) => {
            const isMine = message.role === 'me';
            const time = message.timestamp
               ? dayjs(message.timestamp).format('h:mm A')
               : '';

            const prevMessage = index > 0 ? messages[index - 1] : null;
            const showDateSeparator = !prevMessage ||
               dayjs(message.timestamp).format('YYYY-MM-DD') !== dayjs(prevMessage.timestamp).format('YYYY-MM-DD');

            const repliedToMessage = message.reply_to ? findMessageById(message.reply_to) : null;

            return (
               <React.Fragment key={message.id || index}>
                  {showDateSeparator && (
                     <div className="flex justify-center my-4">
                        <span className="px-3 py-1 text-[11px] font-medium text-gray-500 bg-white/80 rounded-lg shadow-sm backdrop-blur-sm uppercase tracking-wider">
                           {getDateLabel(message.timestamp)}
                        </span>
                     </div>
                  )}

                  <div
                     className={`group relative flex flex-col max-w-[85%] sm:max-w-[70%] ${isMine ? 'self-end items-end' : 'self-start items-start'}`}
                     ref={index === messages.length - 1 ? lastMessageRef : null}
                  >
                     <div
                        className={`relative px-3 py-1.5 shadow-sm ${isMine
                           ? 'bg-[#dcf8c6] text-[#303030] rounded-l-lg rounded-tr-lg'
                           : 'bg-white text-[#303030] rounded-r-lg rounded-tl-lg'
                           }`}
                        style={{
                           borderBottomRightRadius: isMine ? '2px' : '10px',
                           borderBottomLeftRadius: isMine ? '10px' : '2px',
                           opacity: message.is_deleted ? 0.7 : 1,
                        }}
                     >
                        {/* Pinned/Starred indicators */}
                        <div className="absolute top-1 right-2 flex gap-1 items-center">
                           {message.is_pinned && <Pin size={10} className="text-gray-400 rotate-45" />}
                           {message.is_starred && <Star size={10} className="text-orange-400 fill-orange-400" />}
                        </div>

                        {/* Dropdown trigger - appears on hover */}
                        {!message.is_deleted && (
                           <div className={`absolute top-1 ${isMine ? 'left-[-32px]' : 'right-[-32px]'} opacity-0 group-hover:opacity-100 transition-opacity z-10`}>
                              <IconButton
                                 size="small"
                                 onClick={(e) => handleOpenMenu(e, message)}
                                 sx={{
                                    color: 'gray',
                                    padding: '4px',
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' }
                                 }}
                              >
                                 <ChevronDown size={14} />
                              </IconButton>
                           </div>
                        )}

                        {/* Reply Quote Section */}
                        {repliedToMessage && (
                           <div
                              className={`mb-1.5 p-2 rounded-md bg-black/5 border-l-4 ${isMine ? 'border-[#075e54]' : 'border-[#25d366]'} cursor-pointer hover:bg-black/10 transition-colors`}
                              onClick={() => {
                                 const element = document.getElementById(`msg-${message.reply_to}`);
                                 element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                 element?.classList.add('animate-pulse-brief');
                                 setTimeout(() => element?.classList.remove('animate-pulse-brief'), 2000);
                              }}
                           >
                              <div className={`text-[12px] font-bold ${isMine ? 'text-[#075e54]' : 'text-[#25d366]'}`}>
                                 {repliedToMessage.role === 'me' ? 'You' : 'Others'}
                              </div>
                              <div className="text-[12.5px] text-gray-600 line-clamp-2 italic">
                                 {repliedToMessage.is_deleted ? 'Message deleted' : repliedToMessage.content}
                              </div>
                           </div>
                        )}

                        <div
                           id={`msg-${message.id}`}
                           className={`text-[14.5px] leading-relaxed pr-1 ${message.is_deleted ? 'italic text-gray-500 flex items-center gap-1.5' : ''}`}
                        >
                           {message.is_deleted ? (
                              <>
                                 <Trash2 size={13} />
                                 <span>{message.content || "This message was deleted"}</span>
                              </>
                           ) : (
                              <ReactMarkdown
                                 components={{
                                    p: ({ children }) => <span className="m-0">{children}</span>
                                 }}
                              >
                                 {message.content}
                              </ReactMarkdown>
                           )}
                        </div>

                        <div className="flex items-center justify-end gap-1 mt-1 -mr-1">
                           <span className="text-[10px] text-gray-500 font-medium whitespace-nowrap">
                              {time}
                           </span>
                           {isMine && !message.is_deleted && <MessageTick status={message.status} />}
                        </div>
                     </div>
                  </div>
               </React.Fragment>
            );
         })}

         <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleCloseMenu}
            PaperProps={{
               elevation: 4,
               sx: {
                  borderRadius: '12px',
                  minWidth: '190px',
                  mt: 0.5,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  '& .MuiMenuItem-root': {
                     fontSize: '14.5px',
                     py: 1.2,
                     px: 2,
                  }
               }
            }}
         >
            <MenuItem onClick={handleCloseMenu}>
               <ListItemIcon sx={{ minWidth: '36px !important' }}><Info size={18} /></ListItemIcon>
               <ListItemText>Message info</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleReply}>
               <ListItemIcon sx={{ minWidth: '36px !important' }}><Reply size={18} /></ListItemIcon>
               <ListItemText>Reply</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleCopy}>
               <ListItemIcon sx={{ minWidth: '36px !important' }}><Copy size={18} /></ListItemIcon>
               <ListItemText>Copy</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleCloseMenu}>
               <ListItemIcon sx={{ minWidth: '36px !important' }}><Smile size={18} /></ListItemIcon>
               <ListItemText>React</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleCloseMenu}>
               <ListItemIcon sx={{ minWidth: '36px !important' }}><Forward size={18} /></ListItemIcon>
               <ListItemText>Forward</ListItemText>
            </MenuItem>
            <MenuItem onClick={handlePin}>
               <ListItemIcon sx={{ minWidth: '36px !important' }}><Pin size={18} /></ListItemIcon>
               <ListItemText>{selectedMessage?.is_pinned ? 'Unpin' : 'Pin'}</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleStar}>
               <ListItemIcon sx={{ minWidth: '36px !important' }}><Star size={18} /></ListItemIcon>
               <ListItemText>{selectedMessage?.is_starred ? 'Unstar' : 'Star'}</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
               <ListItemIcon sx={{ minWidth: '36px !important' }}><Trash2 size={18} color="#ef4444" /></ListItemIcon>
               <ListItemText sx={{ color: '#ef4444' }}>Delete for me</ListItemText>
            </MenuItem>
         </Menu>
      </div>
   );
};

export default ChatMessages;